/* eslint-disable complexity */
import { Chart, ChartLayout } from '@tencent/chart';
import { Container } from '@tencent/alloy-renderer';
import {
    ChartConfigFactory,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/chart-config-factory';
import i18n from 'i18next';
import {
    getChartBox,
    getChartTitleBox,
    getPlotAreaLayoutBox,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/layout';
import {
    createImageSource,
    createImageSourceWithSrc,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/utils/image';
import { RenderPostionType } from '@src/js/constant';
import type {
    ChartPlotComponentProps,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-plot-component';
import {
    ChartPlotComponent,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-plot-component';
import { TaskQueueManager } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-task-manager';
import { getPixelRatio, lowMemoryMode } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/utils';
import type {
    CanvasObject,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-canvas-pool-manager';
import {
    CanvasPoolManager,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-canvas-pool-manager';
import debounce from 'lodash/debounce';
import { HitAreaContainer } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/hit-area-component';
import { ChartElementManager } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-elements';
import { CommonCombineConfigHelper } from './chart-config/common-combine-config/common-combine-config-helper';
import type { BaseChartConfigHelper } from './chart-config/base-chart-config-helper';
import type { IChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-model.interface';
import type { ChartEditLogic } from '@src/slide/content-viewer/service/canvas-renderer/entry/pc-edit/logic/chart-edit-logic';
import type {
    ChartComponentProps,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/interface/chart-component.interface';
import type {
    IChartConfigHelper,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/interface/chart-config.interface';
import type { RendererChartComponent } from '../../elements/renderer-chart-component';
import type { ComboChartConfigHelper } from './chart-config/combo-chart-config-helper';
import type { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import type {
    IChartElement,
    IChartElementGroup,
    SelectedElement,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-interaction.interface';
import {
    ChartElementType,
    SpecialGeometryType,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-interaction.interface';
import type {
    ElementBox,
    GroupingRule,
    IChartConfig,
    IChartInitialConfig,
} from '@tencent/chart/lib/chart-ui/interface';
import { Chart as G2Chart } from '@tencent/chart/lib/antv/g2';
import {
    BorderType,
    ElementType,
    GroupingResult,
    IGeometryElement,
    IGroupSelectElement,
    ISelectElement,
    ISelectEventData,
} from '@tencent/chart/lib/chart-ui/interface';
import { IFrameInfo } from '@editor/src/page/index/components/canvas/element-wrapper/utils';
import AlloyRenderContext
    from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/custom-render/render-context';
import ChartContext from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/custom-render/chart-context';
import ChartRender from '@tencent/docs-chart-render';
import { isLineShape } from './chart-elements/base';
import {
    seriesIdGrouping,
    specialTypeElementsGrouping,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-elements/grouping-rules';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { FORBID_WEBGL_FILTER } from '@src/slide/infrastructure/module/dependent/config/renderer/switch';
import { ICustomEvent } from '@tencent/chart/lib/antv/g2/interface';

type RenderResolver = () => void;
/**
 * 是否使用自定义渲染，使用统一渲染层渲染。false则继续使用G2内部的canvas渲染
 */
const USE_CUSTOM_RENDER = true;
/**
 * 是否使用chart render组件进行渲染。自定义渲染封装成的组件。
 */
const USE_CHART_RENDER_COMPONENT = true;
const USE_SELECT_EVENT = true;

export class ChartContainer extends Container {
    public model: IChartModel;
    public factoryOptions: InterfaceFactoryOptions;

    // 用于生成对应图表逻辑的chartConfig的helper实例
    public chartConfigHelper: BaseChartConfigHelper | ComboChartConfigHelper | undefined;
    public chartConfig: IChartConfig | undefined;

    // 逆向计算的图表布局数据
    public chartLayout: ChartLayout;
    // @tencent/chart 图表实例
    public chartInstance: Chart | undefined;
    public canvasObject: CanvasObject | undefined;
    // g2图表实例
    public g2Chart: G2Chart | undefined;
    // 编辑时，上一次渲染的G2实例
    public preG2Chart: G2Chart | undefined;
    // 存储图表中所有图表元素的数据集合，用于上层进行选中判定
    public chartElements: Array<IChartElement | IChartElementGroup>;

    private wholeChart: RendererChartComponent;

    // 这个和drawChartPromise有所区别
    // renderStatus是组件维度的，用于判定图表是否处于渲染更新流程，用于上层做一些统一的逻辑判定，比如是否需要等待图表渲染完毕
    // drawChartPromise目前是用于判定g2的渲染状态，而组件自身还有一些异步调度，不等同于组件的渲染状态
    private renderStatus?: Promise<undefined>;
    private renderResolver?: RenderResolver;

    private chartElementsManager: ChartElementManager;
    // 所有元素点击区域的容器
    private hitAreaContainer: HitAreaContainer;

    // 绘制g2 canvasElement到slide画布上的组件
    private plotComponent: ChartPlotComponent;

    // 传给自定义渲染节点的上下文信息
    private chartContext?: ChartContext;
    private chartRender?: ChartRender;

    private drawChartPromise: Promise<unknown> | undefined;

    private resizing = false;
    private lastRenderFrame: IFrameInfo;

    private debouncedUpdate: () => void;
    private chartBox: ElementBox;

    private isEditable = false;

    public constructor(props: ChartComponentProps) {
        super();

        this.debouncedUpdate = USE_CUSTOM_RENDER ? () => this.updateChartPlot()
            : debounce(() => this.updateChartPlot(), 200);
        this.updateProps(props);
        this.chartElementsManager = new ChartElementManager();
    }

    public updateChartPlot() {
        if (this.wholeChart.isActualRenderChart) {
            if (lowMemoryMode) {
                TaskQueueManager.getInstance().runTask(() => this.updateChart());
            } else {
                this.updateChart();
            }
        } else {
            this.updateFallbackImage();
            // 在兜底图情况，同样设置渲染流程的状态为"结束"
            this.chartRenderComplete();
        }
    }

    public updateProps(props: ChartComponentProps, forceUpdate?: boolean) {
        const { frame, model, factoryOptions, titleRect, wholeChart } = props;
        this.factoryOptions = factoryOptions;
        this.wholeChart = wholeChart;
        const shouldUpdateSource = forceUpdate ?? this.shouldUpdateSource(frame, model);

        this.model = model;
        // 记录渲染frame，用于组合中变更大小时，判定大小是否变更
        this.lastRenderFrame = frame;

        if (shouldUpdateSource) {
            this.clearExpiredLayout();

            const chartBox = getChartBox(this.model.props.style);
            const titleBox = getChartTitleBox(this.model.title, titleRect);
            const plotManualLayoutBox = getPlotAreaLayoutBox(chartBox, this.model.plotArea.layout);
            const layoutBoxes = {
                chartBox,
                titleBox,
                plotManualLayoutBox,
            };
            this.chartBox = chartBox;
            this.chartLayout = new ChartLayout(layoutBoxes);

            this.debouncedUpdate();
        }
    }

    public updateFallbackImage() {
        const { width, height } = this.chartBox;

        this.updatePlotComponent({
            height,
            width,
            resource: this.createFallbackImageSource(),
            factoryOptions: this.factoryOptions,
        });

        this.resetShouldUpdate();
    }

    public createFallbackImageSource() {
        const { width, height } = this.chartBox;
        const { fallbackImageSrc } = this.model;

        // 如果有fallbackImageSrc就渲染aspose的兜底图表，否则显示兜底文字
        if (fallbackImageSrc) {
            return createImageSourceWithSrc(fallbackImageSrc);
        }

        return createImageSource(i18n.t('暂不支持此图表'), width, height);
    }

    public updateChart = async () => {
        // 为了防止上层获取到的状态一直被阻塞，加一层try catch来避免render状态异常
        try {
            this.chartConfigHelper = ChartConfigFactory.getConfigHelperWithModel(this.model, this.chartLayout);
            // 获取common config时会计算legend box大小
            const commonConfig = new CommonCombineConfigHelper(
                this.chartConfigHelper as IChartConfigHelper,
                this.model.getContext(),
            ).getCommonConfig();
            const config = this.chartConfigHelper?.getConfig();
            if (config) {
                config.commonConfig = commonConfig;
            }
            this.chartConfig = config;

            this.destroyPreChart();  // 如果已经有上一次的G2实例，destory
            this.preG2Chart = this.g2Chart;  // 保存上一次渲染的G2实例

            this.drawChartPromise = this.drawSourceChart();

            await this.drawChartPromise;
        } catch (err) {
            console.error(err);
            window.log.error('updateChart error', err);
            this.chartRenderFailed();
        }

        // 设置渲染流程的状态为"结束"
        this.chartRenderComplete();
    };

    // 判断是否需要更新g2的原canvas对象，此对象用于displayObject的上下文绘制
    public shouldUpdateSource(nextFrameValue: any, model: IChartModel): boolean {
        const isCurrentResizing = this.factoryOptions.editingStateManager?.isResizing ?? false;
        const isPreResizing = this.resizing;
        const isCurrentIsEditable = !!this.factoryOptions.isEditable;
        const isPreIsEditable = this.isEditable;
        this.resizing = isCurrentResizing;
        this.isEditable = isCurrentIsEditable;

        // 如果权限发生变更，需要重新渲染
        if (isPreIsEditable !== isCurrentIsEditable) {
            return true;
        }

        // resize引起的变更，等resize结束再绘制
        if (isCurrentResizing) {
            return false;
        }

        // 上一次是resizing,这次是不是，会触发 两次update。只调用一次，结束那一次不触发
        if (isPreResizing && !isCurrentResizing) {
            return false;
        }

        // 可能多个渲染树复用一个model，可能数据未变更
        // 所以当渲染的时候如果plotComponent，就渲染，比如"板式"
        if (!this.plotComponent && !USE_CUSTOM_RENDER) {
            return true;
        }

        // 组合变更大小，不会走图表model的sdkOnDidChange，这里需要额外判断宽高是否变化
        const { width, height } = this.lastRenderFrame || {};
        const { width: nextWidth, height: nextHeight } = nextFrameValue;
        if (width !== nextWidth || height !== nextHeight) {
            return true;
        }

        // 非resize引起的update，通过changeSet变更来判定是否需要重新绘制source
        return model.shouldChartUpdate;
    }

    // TODO 这里要注意下canvas的内存问题
    public async drawSourceChart() {
        if (!this.chartConfig) {
            console.error(i18n.t('slide不支持的图表类型'), this.model);
            return;
        }

        this.destroyPreChart();

        await this.renderChartResource(this.chartConfig);

        // changeSet触发的更新，左侧缩略图较右侧编辑区延迟两个事件轮询，躲开编辑区的构建和渲染
        // 在侧左侧缩略图更新后，标记更新完成，防止后续再数据未变更情况下，走了updateProps导致图表重新渲染
        return addSyncTask(() => {
            this.keepPrevSelectState();
            this.resetShouldUpdate();
        });
    }

    public resetSelectState() {
        const selectManager = this.chartInstance?.getSelectManager();
        selectManager?.resetSelectState();
    }

    // 保存上一次的选中状态，用于在数据变更后，选中状态不变
    // g2的数据标签数据同步获取不到，需要异步获取，暂时先这么解决
    private keepPrevSelectState() {
        const isChartElementSelectable = this.factoryOptions.region === RenderPostionType.CANVAS;
        if (isChartElementSelectable) {
            // 如果有留存的selectElement，需要使用这份数据在新图表中去初始化选中状态
            // 这种场景一般为，在选中元素的情况下，更新图表
            const oldSelectedElement = this.model.selectedElement;
            if (oldSelectedElement) {
                this.initSelectState(oldSelectedElement); // 更换为同步后，看看能否拿到正确的值 拿到正确的值
            }
        }
    }

    private initSelectState(oldSelectedElement?: SelectedElement) {
        if (oldSelectedElement?.type === ChartElementType.TITLE) {
            // 当前title由slide组件绘制和事件管理，后续如果通过chart库实现则移除这个限制逻辑
            return;
        }

        const selectManager = this.chartInstance?.getSelectManager();
        selectManager?.updateWithSelectEventData(oldSelectedElement?.selectEventData);
    }

    private chartRenderComplete() {
        this.model.resolveChartRender();
    }

    private chartRenderFailed() {
        this.model.rejectChartRender();
    }

    private destroyExpiredChart(g2Chart?: G2Chart) {
        // lowMemoryMode的情况会在releaseChartResource中destroy
        if (!lowMemoryMode || USE_CUSTOM_RENDER) {
            g2Chart?.destroy();
            this.chartContext?.disposeEvent();
        }
    }

    private getChartResourceCanvas() {
        return this.g2Chart!.ele?.getElementsByTagName('canvas')[0];
    }

    private async renderChartResource(chartConfig: IChartConfig) {
        const { width, height } = this.chartBox;
        let chartContainer;
        let renderContext;
        if (!USE_CUSTOM_RENDER) {
            chartContainer = document.createElement('div');
            Object.assign(chartContainer.style, {
                width: `${width}px`,
                height: `${height}px`,
            });
        } else {
            if (!USE_CHART_RENDER_COMPONENT) {
                this.chartContext = new ChartContext({
                    getThumbPageScale4PatternFill: this.getThumbPageScale,
                });
                renderContext = new AlloyRenderContext(this.chartContext);
                chartContainer = renderContext.createContainer();
                chartContainer.element = this;
            } else {
                this.chartRender = new ChartRender({
                    container: this,
                    forbidWebgl: FORBID_WEBGL_FILTER,
                    getScaleForPatternFill: this.getThumbPageScale,
                });
            }
        }

        const chartParams: IChartInitialConfig = {
            selectBorderContainer: `selected-chart-wrapper-${this.model.key}`,
            container: chartContainer,
            autoFit: false,
            useDomTitle: false,  // 不使用默认的dom title
            width: Math.floor(width),
            height: Math.floor(height),
            syncViewPadding: true,
            chartLayout: this.chartLayout,
            renderContext,
            renderer: USE_CUSTOM_RENDER ? 'custom' : 'canvas',
            defaultInteractions: [],  // slide暂时移除默认组件相关的事件
            disableInteractions: true,
            ...this.getInteractionConfigs(),
            ...this.chartRender?.getChartConfig(),
        };

        if (lowMemoryMode && !USE_CUSTOM_RENDER) {
            if (!this.canvasObject) {
                // 在这申请canvas资源。结束之后释放
                const canvas = await CanvasPoolManager.getInstance().applyCanvas({
                    memory: this.calculateCanvasMemory(),
                    onForceFree: async () => {
                        try {
                            // 等待当前的g2绘制结束之后，才去释放
                            await this.drawChartPromise;
                            await this.plotComponent.transformResourceToImage();
                            this.releaseChartResource();
                        } catch (err) {
                            console.error(err);
                        }
                    },
                });
                this.canvasObject = canvas;
            }

            this.chartInstance = new Chart({
                ...chartParams,
                canvas: this.canvasObject?.instance,
            });
        } else {
            this.chartInstance = new Chart(chartParams);
        }

        this.g2Chart = this.chartInstance.getAntVChart();
        this.g2Chart.animate(false);  // 需要关闭g2动画，防止不必要的消耗
        // 注册自定义渲染事件
        this.chartRender?.initEvent(this.g2Chart);

        if (USE_SELECT_EVENT) {
            this.customSelectEvent();
        }

        this.chartInstance.draw(chartConfig);
    }

    private getDragCfg(): IChartInitialConfig['dragCfg'] {
        return {
            onDragStart: (e) => {
                this.chartEditLogic?.handleInnerDragStart(e);
            },
            onDrag: (e) => {
                this.chartEditLogic?.handleInnerDrag(e);
            },
            onDragEnd: (e) => {
                this.chartEditLogic?.handleInnerDragEnd(e);
            },
            onChange: (element, rect, diff) => {
                const hasOffsetChange = diff.height !== 0 || diff.width !== 0 || diff.x !== 0 || diff.y !== 0;
                if (hasOffsetChange) {
                    this.chartEditLogic?.handleElementLayoutChange(element, rect, diff);
                }
            },
        };
    }

    private getResizeCfg(): IChartInitialConfig['resizeCfg'] {
        return {
            onResizeStart: e => e.stopPropagation(),
            onResize: e => e.stopPropagation(),
            onChange: (element, rect, diff) => {
                const hasOffsetChange = diff.height !== 0 || diff.width !== 0 || diff.x !== 0 || diff.y !== 0;
                if (hasOffsetChange) {
                    this.chartEditLogic?.handleElementLayoutChange(element, rect, diff);
                }
            },
        };
    }

    private getSelectCfg(): IChartInitialConfig['selectCfg'] {
        return {
            legend: {
                singleSelectable: false,
            },
            getScale: () => this.factoryOptions.getPageScale(),
        };
    }

    private getInteractionConfigs() {
        if (!this.isChartEditable) {
            return {
                renderSelectBorder: false,
            };
        }

        return {
            renderSelectBorder: true,
            selectCfg: this.getSelectCfg(),
            dragCfg: this.getDragCfg(),
            resizeCfg: this.getResizeCfg(),
        };
    }

    // 在基础的选中事件上，注册一些相对特殊的规则
    private customSelectEvent() {
        if (!this.isChartEditable) {
            return;
        }

        this.registerCustomElementProcess();
        this.registerSpecialElementGroupingRule();
        this.addSelectElementObserver();
    }

    private get isChartEditable() {
        return this.model?.currentEditState === PPTEditorStateType.EDITABLE;
    }

    private getThumbPageScale = (): number => {
        const { region, getPageScale } = this.factoryOptions;
        if (region === RenderPostionType.LEFTLIST) {
            const scale = getPageScale?.();
            if (scale) {
                return scale * 2.5;
            }
        }
        return 1;
    };

    private registerCustomElementProcess() {
        const selectManager = this.chartInstance?.getSelectManager();
        selectManager?.registerElementProcess<IGeometryElement>('geometry', (element: IGeometryElement) => {
            // 如果是趋势线，需要根据office的表现，改成端点边缘选中
            if (
                isLineShape(element.shapeType)
                && (element.data[0])?.specialGeometryType === SpecialGeometryType.trendLine
            ) {
                return {
                    ...element,
                    borderType: BorderType.EDGE,
                };
            }

            return element;
        });
    }

    private registerSpecialElementGroupingRule() {
        const selectManager = this.chartInstance?.getSelectManager();

        const groupingRule: GroupingRule<IGeometryElement> = (
            targetCfg: any,
            allElements: IGeometryElement[],
            groupType: ElementType,
        ): GroupingResult<IGeometryElement> => {
            // TODO 禁止单独选中和禁止组选中的规则，可以放在 groupingRule中的字段上。
            //  禁止单独选中就在group上加个字段
            //  禁止组选中，就每次的groupRule只返回他自己
            const { data } = targetCfg.origin;
            const { specialGeometryType } = Array.isArray(data) ? data[0] : (data || {});

            if (specialGeometryType) {
                const groupingResult = specialTypeElementsGrouping(allElements, data);
                if (groupingResult) {
                    return groupingResult;
                }
            }

            return seriesIdGrouping(targetCfg, allElements, data, groupType) as GroupingResult<IGeometryElement>;
        };

        selectManager?.registerGroupingRule('geometry', groupingRule);
    }

    private addSelectElementObserver() {
        this.chartInstance?.on('select-change', (event: ISelectEventData) => {
            console.log('chart select-change', event);

            const { data, elementType, stage } = event;
            if (stage === 'beforedestroy' || this.chartEditLogic?.isMultiSelected()) {
                return;
            }

            if (this.isSelectGroup(data)) {
                const group = data.elements.map(ele => this.chartElementsManager.convertToChartElement(ele));
                this.model.markSelected({
                    group,
                    id: data.groupId,
                    type: group[0].type,
                    selectEventData: event,
                });
            } else if (data) {
                this.model.markSelected({
                    ...this.chartElementsManager.convertToChartElement(data),
                    selectEventData: event,
                });
            } else {
                this.model.removeAllSelectedElements();
            }

            if (!this.model.isElementSelected(ChartElementType.TITLE)) {
                this.chartEditLogic?.triggerTitleBlur();
            }
        });

        this.chartInstance?.on('mousedown', () => {
            this.chartEditLogic?.handleChartInstanceMouseDown?.();
        });
    }

    private isSelectGroup(element: ISelectElement | IGroupSelectElement | null): element is IGroupSelectElement {
        return !!(element as IGroupSelectElement)?.elements;
    }

    private async releaseChartResource() {
        await this.drawChartPromise;

        this.destroyPreChart();
        this.g2Chart?.destroy();
        this.g2Chart = undefined;
        this.chartInstance = undefined;
        this.drawChartPromise = undefined;
        this.chartConfig = undefined;

        if (this.canvasObject) {
            const { canvasObject } = this;
            this.canvasObject = undefined;

            CanvasPoolManager.getInstance().releaseCanvas(canvasObject);
        }
    }

    private resetShouldUpdate() {
        if (this.factoryOptions.region === RenderPostionType.CANVAS) {
            // 在更新完之后，标记更新完成，防止后续再数据未变更情况下，走了updateProps导致图表重新渲染
            this.model.setChartHasUpdated(true);
        }
    }

    // 重新收集图表中的各个元素的大小位置信息
    private updateChartElements() {
        this.chartElements = this.getChartElements();
    }

    private updateHitArea() {
        const hitAreaPropList = this.chartEditLogic?.getChartHitAreaProps(this.chartElements);
        if (hitAreaPropList) {
            const { width, height } = this.chartLayout.g2CanvasBox;
            if (!this.hitAreaContainer) {
                this.hitAreaContainer = new HitAreaContainer({
                    width, height, factoryOptions: this.factoryOptions, hitAreas: hitAreaPropList,
                });
                this.addChild(this.hitAreaContainer);
            } else {
                this.removeChild(this.hitAreaContainer);
                this.addChild(this.hitAreaContainer);  // 先移除再添加保证始终在最上层
                this.hitAreaContainer.updateProps({
                    width, height, factoryOptions: this.factoryOptions, hitAreas: hitAreaPropList,
                });
            }
        }
    }

    private get chartEditLogic(): ChartEditLogic | undefined {
        return this.factoryOptions.logicService.getLogic(this.factoryOptions) as ChartEditLogic;
    }

    private updatePlotComponent(props: ChartPlotComponentProps) {
        if (!this.plotComponent) {
            this.plotComponent = new ChartPlotComponent(props);
            this.addChild(this.plotComponent);
        } else {
            this.plotComponent.updateProps(props);
        }
    }

    private clearExpiredLayout() {
        // @ts-ignore
        this.chartLayout = undefined;
    }

    private getChartElements() {
        const chart = this.g2Chart;
        if (!chart) {
            return [];
        }

        if (!this.chartElementsManager) {
            this.chartElementsManager = new ChartElementManager();
        }

        return this.chartElementsManager.getChartElements(chart);
    }

    private calculateCanvasMemory() {
        const { width, height } = this.chartBox;

        return width * height * getPixelRatio() * 4;
    }

    private scaleCanvas(g2Canvas: G2Chart['canvas']) {
        const scale = this.factoryOptions.getPageScale();
        if (!lowMemoryMode || scale >= 1) {
            return;
        }

        const dpi = getPixelRatio();
        g2Canvas.changeSize(
            this.chartBox.width * scale * dpi,
            this.chartBox.height * scale * dpi,
        );
        g2Canvas.scale(scale * dpi);
    }

    /**
     * destroy上一次渲染的图表实例，目前每次编辑都会生成新的图吧实例
     * @private
     */
    private destroyPreChart() {
        if (this.preG2Chart) {
            this.destroyExpiredChart(this.preG2Chart);
            this.preG2Chart = undefined;
        }
    }
}

export const addSyncTask = (task: () => any) => new Promise((resolve) => {
    setTimeout(() => {
        resolve(task());
    }, 50);
});
